// Top-of-viewport slide-down toast renderer. Subscribes once at the
// WidgetShell level; any component can fire a toast via the
// `pushToast(message, tone?)` helper in state/toast.
//
// One active toast at a time — a new push replaces the current one
// and resets the auto-hide timer. The prototype's toast pattern is
// inherently transient ("Added to cart") so a queue would feel slow.

import { useEffect, useState } from "react";
import {
  type ToastEvent,
  type ToastTone,
  subscribeToToasts,
} from "../state/toast";
import { WidgetLayer } from "./WidgetLayer";

const VISIBLE_MS = 2400;

interface ActiveToast {
  id: number;
  message: string;
  tone: ToastTone;
  visible: boolean;
}

export function ToastContainer() {
  const [active, setActive] = useState<ActiveToast | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToToasts((event: ToastEvent) => {
      setActive({
        id: event.id,
        message: event.message,
        tone: event.tone,
        visible: true,
      });
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!active?.visible) return;
    const id = active.id;
    // Auto-hide — slide out after VISIBLE_MS. The unmount happens on
    // the next toast push (replaces this one) OR after a follow-up
    // setTimeout that gives the CSS transition time to finish.
    const hideTimer = window.setTimeout(() => {
      setActive((prev) => (prev && prev.id === id ? { ...prev, visible: false } : prev));
    }, VISIBLE_MS);
    const unmountTimer = window.setTimeout(() => {
      setActive((prev) => (prev && prev.id === id ? null : prev));
    }, VISIBLE_MS + 400);
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(unmountTimer);
    };
  }, [active?.id]);

  if (!active) return null;

  return (
    <WidgetLayer>
      <div
        className={`vt-toast vt-toast-${active.tone}${active.visible ? " vt-toast-show" : ""}`}
        role="status"
        aria-live="polite"
      >
        <span className="vt-toast-icon" aria-hidden>
          {active.tone === "warn" ? "⚠" : "✓"}
        </span>
        <span className="vt-toast-message">{active.message}</span>
      </div>
    </WidgetLayer>
  );
}
